/**
 * React Query hooks for Email SLO dashboard endpoints.
 */

import { useQuery } from '@tanstack/react-query';

import { useTenant } from '@/contexts/TenantContext';
import apiClient from '@/lib/api-client';

export type DashboardPeriod = '24h' | '7d' | '30d';

export interface EmailMetricsResponse {
  region: string;
  slo_percent: number;
  emails_sent: number;
  bounce_rate: number;
  complaint_rate: number;
  provider_breakdown: Record<string, number>;
  latency_p50_ms: number;
  latency_p95_ms: number;
  latency_p99_ms: number;
  alerts: Array<Record<string, unknown>>;
  timestamp: string;
}

export interface EmailMetricsTrendPoint {
  timestamp: string;
  slo_percent: number;
  emails_sent: number;
  bounce_rate: number;
  complaint_rate: number;
  alerts_count: number;
}

export interface EmailMetricsTrendResponse {
  region: string;
  period: DashboardPeriod;
  granularity: 'hour' | 'day';
  points: EmailMetricsTrendPoint[];
}

export interface EmailRegionalDashboardData {
  metrics: EmailMetricsResponse[];
  trends: EmailMetricsTrendResponse[];
}

export interface SLORegionResponse {
  region: string;
  slo_percent: number;
  status: 'HEALTHY' | 'MONITOR' | 'DEGRADED';
}

export interface EmailDashboardOverviewResponse {
  global_slo: number;
  global_status: 'HEALTHY' | 'MONITOR' | 'DEGRADED';
  regions: SLORegionResponse[];
  last_updated: string | null;
  alerts_count: number;
}

export function useEmailSLOOverview() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery<EmailDashboardOverviewResponse>({
    queryKey: ['email-slo-overview', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: EmailDashboardOverviewResponse }>('/api/v1/dashboard/emails/slo');
      return data.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useEmailRegionalDashboard(period: DashboardPeriod = '7d') {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['email-regional-dashboard', tenantId, period],
    enabled: !!tenantId,
    queryFn: async (): Promise<EmailRegionalDashboardData> => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }

      const [metricsResp, trendsResp] = await Promise.all([
        apiClient.get<{ data: EmailMetricsResponse[] }>(`/api/v1/dashboard/emails/metrics?period=${period}`),
        apiClient.get<{ data: EmailMetricsTrendResponse[] }>(`/api/v1/dashboard/emails/trends?period=${period}`),
      ]);

      return { metrics: metricsResp.data.data, trends: trendsResp.data.data };
    },
    staleTime: 60 * 1000,
  });
}
